/**
 * Import Cline hook scripts from project (.clinerules/hooks/) and global
 * (Documents/Cline/Hooks/) directories into canonical hooks.yaml.
 *
 * Scripts are generated by agentsmesh and embed metadata as comments:
 *   # agentsmesh-event:   <canonical event name>
 *   # agentsmesh-matcher: <matcher pattern>
 *   # agentsmesh-command: <shell command>
 */

import { join, basename, dirname } from 'node:path';
import { stringify as yamlStringify } from 'yaml';
import type { ImportResult } from '../../core/types.js';
import {
  readDirRecursiveNoSymlinks,
  readFileSafe,
  writeFileAtomic,
  mkdirp,
} from '../../utils/filesystem/fs.js';
import {
  CLINE_TARGET,
  CLINE_HOOKS_DIR,
  CLINE_GLOBAL_HOOKS_DIR,
  CLINE_CANONICAL_HOOKS,
} from './constants.js';

function extractMeta(content: string, key: string): string | null {
  const match = content.match(new RegExp(`^# agentsmesh-${key}:\\s*(.+)$`, 'm'));
  return match?.[1]?.trim() ?? null;
}

async function loadHooksFromDir(
  dir: string,
  hooks: Record<string, Array<{ matcher: string; command: string }>>,
): Promise<void> {
  const files = await readDirRecursiveNoSymlinks(dir).catch(() => []);
  const shFiles = files.filter((f) => basename(f).endsWith('.sh') && dirname(f) === dir);
  for (const srcPath of shFiles) {
    const content = await readFileSafe(srcPath);
    if (!content) continue;
    const event = extractMeta(content, 'event');
    const command = extractMeta(content, 'command');
    if (!event || !command) continue;
    const matcher = extractMeta(content, 'matcher') ?? '*';
    if (!hooks[event]) hooks[event] = [];
    hooks[event]!.push({ matcher, command });
  }
}

/**
 * Import Cline hook scripts into canonical hooks.yaml.
 * Reads from both project-level `.clinerules/hooks/` and global `Documents/Cline/Hooks/`.
 *
 * @param projectRoot - Project root directory (or homedir() for global mode)
 * @param results - Import results accumulator
 */
export async function importClineHooks(
  projectRoot: string,
  results: ImportResult[],
): Promise<void> {
  const hooks: Record<string, Array<{ matcher: string; command: string }>> = {};

  await loadHooksFromDir(join(projectRoot, CLINE_HOOKS_DIR), hooks);
  await loadHooksFromDir(join(projectRoot, CLINE_GLOBAL_HOOKS_DIR), hooks);

  if (Object.keys(hooks).length === 0) return;

  const destPath = join(projectRoot, CLINE_CANONICAL_HOOKS);
  await mkdirp(dirname(destPath));
  await writeFileAtomic(destPath, yamlStringify(hooks));
  results.push({
    fromTool: CLINE_TARGET,
    fromPath: join(projectRoot, CLINE_GLOBAL_HOOKS_DIR),
    toPath: CLINE_CANONICAL_HOOKS,
    feature: 'hooks',
  });
}
